import { Query, useQuery } from '@tanstack/react-query'
import { getDemoContributionStatus } from '../api/demo-customer.api'
import { DemoContributionStatusResponse } from '../types'

export const DEMO_CONTRIBUTION_STATUS_QUERY_KEY = (clientPublicKey: string) => [
  'demoContributionStatus',
  clientPublicKey,
]

/** How often to re-check the run while it is in progress. */
export const DEMO_CONTRIBUTION_STATUS_POLL_MS = 5000

interface DemoContributionStatus {
  /** Null when the company never had a demo contribution run. */
  contributionStatus: DemoContributionStatusResponse | null
  /** True while the initial fetch is in flight. */
  isLoading: boolean
}

/**
 * Polls every 5s while the run is in progress; stops on completed, failed or
 * null. Because it fetches on mount, reloading the page mid-run resumes polling.
 */
export function demoContributionStatusRefetchInterval(
  query: Query<DemoContributionStatusResponse | null>
): number | false {
  return query.state.data?.status === 'in-progress'
    ? DEMO_CONTRIBUTION_STATUS_POLL_MS
    : false
}

/**
 * Status of the demo contribution run kicked off by populate.
 * Query key is `['demoContributionStatus', clientPublicKey]` so
 * `useUploadAndPopulateDemoQuote` can invalidate it right after populate.
 */
export function useDemoContributionStatus(
  clientPublicKey: string
): DemoContributionStatus {
  const { data, isLoading } = useQuery({
    queryKey: DEMO_CONTRIBUTION_STATUS_QUERY_KEY(clientPublicKey),
    queryFn: () => getDemoContributionStatus(clientPublicKey),
    refetchInterval: demoContributionStatusRefetchInterval,
  })

  return { contributionStatus: data ?? null, isLoading }
}
